import { useState, useCallback } from "react";
import { FileUploadState } from "datasets/pages/tables/components/file-validation/types";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";

export const useFileUpload = () => {
  const [uploadState, setUploadState] = useState<FileUploadState>({
    selectedFile: null,
    isUploading: false,
  });
  const [uploadError, setUploadError] = useState<string | null>(null);

  /**
   * Upload a file from the user's device (mock for now)
   */
  const uploadFile = useCallback(async (file: File): Promise<FileInfo> => {
    setUploadState((prev) => ({ ...prev, isUploading: true, uploadProgress: 0 }));
    setUploadError(null);

    try {
      // Mock upload progress
      for (let i = 0; i <= 100; i += 10) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        setUploadState((prev) => ({ ...prev, uploadProgress: i }));
      }

      const mockFileInfo: FileInfo = {
        key: file.name,
        type: FileType.FILE,
        size: file.size,
      } as FileInfo;

      setUploadState((prev) => ({
        ...prev,
        selectedFile: mockFileInfo,
        isUploading: false,
        uploadProgress: 100,
      }));

      return mockFileInfo;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Upload failed";
      setUploadError(errorMessage);
      setUploadState((prev) => ({ ...prev, isUploading: false, uploadProgress: 0 }));
      throw error;
    }
  }, []);

  /**
   * Select an existing file from cloud storage.
   * Accepts a FileInfo object directly from the FileTree.
   */
  const selectFromStorage = useCallback((file: FileInfo): void => {
    setUploadError(null);
    setUploadState((prev) => ({ ...prev, selectedFile: file }));
  }, []);

  /**
   * Reset the upload state to initial values
   */
  const resetUpload = useCallback(() => {
    setUploadState({
      selectedFile: null,
      isUploading: false,
      uploadProgress: undefined,
    });
    setUploadError(null);
  }, []);

  return {
    uploadState,
    uploadFile,
    selectFromStorage,
    resetUpload,
    uploadError,
  };
};
